/**
 * terrain_skirt — an apron around a chunk's border that hides LOD cracks.
 *
 * Neighbouring chunks carry different segment counts (48 / 24 / 10 by ring),
 * so their shared edge has a different number of vertices on each side: the
 * fine edge's extra vertices do not lie on the coarse edge's straight segment,
 * and the gap between them is a T-junction you can see the sky (or the void)
 * through. Chunks also re-LOD independently now, so the mismatch moves around
 * rather than sitting still.
 *
 * Edge-stitching would need each chunk to know its neighbours' current LOD and
 * to rebuild whenever any of them changed — a cascade. A SKIRT does not: it is
 * a vertical apron hanging from the chunk's own border, built from the chunk's
 * own data, that simply fills whatever gap appears. It is the standard fix and
 * it is local, which is what makes it cheap.
 *
 * DEPTH is derived from the chunk's own relief. The gap between a fine and a
 * coarse edge is bounded by how much the ground rises and falls inside one
 * coarse quad, so a flat chunk needs almost nothing and a cliff-edge chunk
 * needs metres. A fixed depth would either show cracks in the hills or bury
 * the apron below cave mouths on the flats.
 */
import * as THREE from 'three/webgpu';

/** Never less than this (m) — covers float wobble on genuinely flat ground. */
export const SKIRT_MIN_M = 1.5;
/** Never more than this (m) — a deep apron is visible from below at cliffs. */
export const SKIRT_MAX_M = 24;
/** Fraction of the chunk's own relief to hang down. */
export const SKIRT_RELIEF_FRACTION = 0.35;

/** How far the apron should hang for a chunk with this much vertical relief. */
export function skirtDepthFor(relief: number): number {
  if (!Number.isFinite(relief) || relief <= 0) return SKIRT_MIN_M;
  return Math.min(SKIRT_MAX_M, Math.max(SKIRT_MIN_M, relief * SKIRT_RELIEF_FRACTION));
}

/** The vertex indices along one border of a (seg+1)² grid, in order. */
export function borderIndices(seg: number, side: 'north' | 'south' | 'west' | 'east'): number[] {
  const n = seg + 1;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    out.push(
      side === 'north' ? i                       // row 0
        : side === 'south' ? (n - 1) * n + i     // last row
          : side === 'west' ? i * n              // column 0
            : i * n + (n - 1),                   // column n-1
    );
  }
  return out;
}

/**
 * Append a skirt to a displaced chunk plane, in place.
 *
 * The plane must be the `(seg+1)²` row-major grid `PlaneGeometry(size, size,
 * seg, seg)` produces after `rotateX(-π/2)` — the same layout the terrain
 * worker writes. Every attribute is extended so the apron carries the border
 * vertex's biome weights, road and field masks (it must shade like the ground
 * it hangs from, not like a stripe of default material).
 *
 * Returns the number of skirt vertices added.
 */
export function addChunkSkirt(geo: THREE.BufferGeometry, seg: number, depth: number): number {
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  const n = seg + 1;
  if (pos.count !== n * n || !geo.index) return 0;

  const border = [
    ...borderIndices(seg, 'north'),
    ...borderIndices(seg, 'south'),
    ...borderIndices(seg, 'west'),
    ...borderIndices(seg, 'east'),
  ];
  const added = border.length;
  const base = pos.count;

  // Grow every attribute: each skirt vertex is a copy of its border vertex
  // with Y dropped, so normals/weights/masks match the edge exactly.
  for (const name of Object.keys(geo.attributes)) {
    const a = geo.getAttribute(name) as THREE.BufferAttribute;
    const size = a.itemSize;
    const src = a.array as Float32Array;
    const next = new Float32Array((base + added) * size);
    next.set(src.subarray(0, base * size));
    for (let k = 0; k < added; k++) {
      const from = border[k] * size;
      const to = (base + k) * size;
      for (let c = 0; c < size; c++) next[to + c] = src[from + c];
    }
    const attr = new THREE.BufferAttribute(next, size);
    attr.needsUpdate = true;
    geo.setAttribute(name, attr);
  }
  // Drop the copies. `position` is y-up after the rotate, so index 1 is height.
  const newPos = geo.getAttribute('position') as THREE.BufferAttribute;
  for (let k = 0; k < added; k++) newPos.setY(base + k, newPos.getY(base + k) - depth);

  // Two triangles per border segment, joining edge pair (i, i+1) to their
  // dropped copies. Winding is per side so every apron faces outward.
  const idx = Array.from(geo.index.array as ArrayLike<number>);
  const sides: Array<[number, boolean]> = [[0, true], [n, false], [2 * n, false], [3 * n, true]];
  for (const [offset, flip] of sides) {
    for (let i = 0; i < seg; i++) {
      const a = border[offset + i], b = border[offset + i + 1];
      const a2 = base + offset + i, b2 = base + offset + i + 1;
      if (flip) idx.push(a, a2, b, b, a2, b2);
      else idx.push(a, b, a2, b, b2, a2);
    }
  }
  geo.setIndex(idx);
  return added;
}
